import { Injectable, Renderer2 } from '@angular/core';
import { CeReferenceService } from './ce-reference.service';
import { CeTooltip, CeTooltipData, instanceOfCeTooltipIdLists, CeTooltipIdLists } from '../models/ce-tooltip';
import { ISubscription } from 'rxjs/Subscription';
import { BiblioRecord } from '../models/biblio-record';

@Injectable()
export class CeTooltipService {
    private subscriptions: Array<ISubscription> = [];
    private renderer: Renderer2;
    private refTooltip: CeTooltip;
    private tooltipContainer;
    private loadingBiblioRel = false;

    constructor(
        private ceReferenceService: CeReferenceService
    ) {
    }

    public setRenderer(renderer: Renderer2) {
        this.renderer = renderer;
    }

    public isManuscriptLink(link: string): boolean {
        return link && (link.indexOf('ms_') !== -1 || link.search(/manuscripts[/]list[/][0-9]+/g) !== -1);
    }

    public isBiblioLink(link: string): boolean {
        return link && (link.indexOf('bib_') !== -1 || link.search(/bibliography[/][0-9]+/g) !== -1);
    }

    public isReferenceLink(link: string): boolean {
        return link && (this.isManuscriptLink(link) || this.isBiblioLink(link));
    }

    public showReferenceTooltip(node, link: string) {
        const msIdList = [];
        const bibIdList = [];

        /* Reference identifiers - obtained depending on the type of link */
        if (link.indexOf('/') !== -1) {
            const targetId = link.split('/').pop();

            /* Single reference identifier */
            if (this.isManuscriptLink(link)) { // manuscript reference
                msIdList.push(parseInt(targetId, 10));
            } else if (this.isBiblioLink(link)) { // bibliography reference
                bibIdList.push(parseInt(targetId, 10));
            }

        } else {
            const targetIdList = link.split(':');

            /* Multiple reference identifiers */
            for (const targetId of targetIdList) {
                if (this.isManuscriptLink(link)) { // manuscript reference
                    msIdList.push(parseInt(targetId.substr(3), 10)); // ignore 'ms_'
                } else if (this.isBiblioLink(link)) { // bibliography reference
                    bibIdList.push(parseInt(targetId.substr(4), 10)); // ignore 'bib_'
                }
            }
        }

        /* Show tooltip */
        if (msIdList.length > 0 || bibIdList.length > 0) {
            this.createReferenceTooltip(node, <CeTooltipIdLists>{msIdList, bibIdList});
        } else {
            return false;
        }
    }

    public createReferenceTooltip(node, tooltipData: CeTooltipData) { // msIdList: Array<number> = [], bibIdList: Array<number> = []
        if (this.renderer) {
            const refTooltipId = 'rtt_' + node.id;

            /* Build tooltip container */
            this.initTooltipContainer(node);

            /* Load reference data */
            if (instanceOfCeTooltipIdLists(tooltipData)) {
                this.loadDataFromIdLists(tooltipData.msIdList, tooltipData.bibIdList);
            } else {
                this.loadDataFromText(tooltipData);
            }

            /* Show tooltip */
            this.renderer.appendChild(node, this.tooltipContainer);

            /* Update the reference tooltip */
            this.refTooltip = new CeTooltip(refTooltipId, this.tooltipContainer, node);
        }
    }

    public removeReferenceTooltip() {
        /* Remove current tooltip */
        if (this.refTooltip) {
            this.renderer.removeChild(this.refTooltip.parent, this.refTooltip.container);
            this.refTooltip = null;
            this.tooltipContainer = null;
        }
    }

    private initTooltipContainer(node) {
        const inCeViewer = (
            node.className.indexOf('ref_start') !== -1
            || node.className.indexOf('rdg_wit') !== -1
        );

        /* Remove current showing tooltip */
        this.removeReferenceTooltip();

        /* Viewport position */
        const rect = node.getBoundingClientRect();
        const left = parseInt(rect.left + window.pageXOffset, 10);
        const top = parseInt(rect.top + window.pageYOffset, 10);

        /* Tooltip container */
        this.tooltipContainer = this.renderer.createElement('div');
        this.renderer.addClass(this.tooltipContainer, 'ref_tooltip');
        if (inCeViewer) {
            this.renderer.setStyle(this.tooltipContainer, 'top', (top - (top * 0.6)) + 'px');
        } else {
            const adjustedLeft = left < 200 ? 0 : (left - 200);
            this.renderer.setStyle(this.tooltipContainer, 'top', (top + 25) + 'px');
            this.renderer.setStyle(this.tooltipContainer, 'left', adjustedLeft + 'px');
        }

        /* Close container button */
        const closeButton = this.renderer.createElement('div');
        this.renderer.setAttribute(closeButton, 'class', 'text-right');
        const closeButtonIcon = this.renderer.createElement('i');
        this.renderer.setAttribute(closeButtonIcon, 'class', 'fa fa-window-close-o rtt_close');
        this.renderer.appendChild(closeButton, closeButtonIcon);
        this.renderer.appendChild(this.tooltipContainer, closeButton);
    }

    private loadDataFromIdLists(msIdList: Array<number>, bibIdList: Array<number>) {
        this.unsubscribeAll();
        this.subscriptions.push(
            this.ceReferenceService.getAllReferences(msIdList, bibIdList).subscribe(
                allRefList => {
                    const msRefList = allRefList[0];
                    const bibRefList = allRefList[1];
                    if ((msRefList.length === msIdList.length || msIdList.length === 0)
                        && (bibRefList.length === bibIdList.length || bibIdList.length === 0)) {
                        if (msIdList.length > 0) {
                            /* Referenced manuscripts label */
                            const msLabel = this.renderer.createElement('p');
                            this.renderer.setAttribute(msLabel, 'class', 'ref_ms_label');
                            this.renderer.appendChild(msLabel, this.renderer.createText('Manuscripts'));
                            this.renderer.appendChild(this.tooltipContainer, msLabel);

                            /* Manuscript links */
                            for (const msRef of msRefList) {
                                const msLink = this.renderer.createElement('div');
                                this.renderer.setAttribute(msLink, 'class', 'ref_link_ms');
                                this.renderer.setAttribute(msLink, 'data-target', msRef.linkId);
                                this.renderer.appendChild(msLink, this.renderer.createText('- ' + msRef.label));
                                this.renderer.appendChild(this.tooltipContainer, msLink);
                            }
                        }
                        if (bibIdList.length > 0 && !this.loadingBiblioRel) {
                            /* Referenced bibliography label */
                            const bibLabel = this.renderer.createElement('p');
                            this.renderer.setAttribute(bibLabel, 'class', 'ref_ms_label');
                            this.renderer.appendChild(bibLabel, this.renderer.createText('Bibliography'));
                            this.renderer.appendChild(this.tooltipContainer, bibLabel);

                            /* Bibliography links */
                            for (const bibRef of bibRefList) {
                                // const bibLink = this.renderer.createElement('div');
                                // this.renderer.setAttribute(bibLink, 'class', 'ref_link_bib');
                                // this.renderer.setAttribute(bibLink, 'data-target', bibRef.linkId);
                                // this.renderer.appendChild(bibLink, this.renderer.createText('- ' + bibRef.label));
                                // this.renderer.appendChild(this.tooltipContainer, bibLink);
                                this.renderer.appendChild(
                                    this.tooltipContainer,
                                    this.createTooltipBiblioElement(<BiblioRecord> bibRef.data)
                                );
                            }
                        }
                    }
                },
                err => console.error(err)
            )
        );
    }

    private loadDataFromText(text: string) {
        const textBox = this.renderer.createElement('div');
        this.renderer.setAttribute(textBox, 'class', 'ref_text_box');
        this.renderer.appendChild(textBox, this.renderer.createText(text));
        this.renderer.appendChild(this.tooltipContainer, textBox);
    }

    private createTooltipBiblioElement(bibRec: BiblioRecord, bibElement = null) {
        if (!bibElement) {
            bibElement = this.renderer.createElement('div');
            this.renderer.setAttribute(bibElement, 'class', 'ref_bib_data');
        }

        const authors = bibRec.__resps.filter(resp => resp.fkidbiblio_resp_types === 1).map(resp => resp.name);
        this.appendTooltipBiblioField(bibElement, 'Authors', authors.join(', '));
        this.appendTooltipBiblioField(bibElement, 'Title', bibRec.title);
        this.appendTooltipBiblioField(bibElement, 'Title of journal', bibRec.data_journal_title);
        const editors = bibRec.__resps.filter(resp => resp.fkidbiblio_resp_types === 2).map(resp => resp.name);
        this.appendTooltipBiblioField(bibElement, 'Editors', editors.join(', '));
        const translators = bibRec.__resps.filter(resp => resp.fkidbiblio_resp_types === 3).map(resp => resp.name);
        this.appendTooltipBiblioField(bibElement, 'Translators', translators.join(', '));
        this.appendTooltipBiblioField(bibElement, 'Title of volume', bibRec.data_volume_title);
        this.appendTooltipBiblioField(bibElement, 'Year', bibRec.data_year);
        this.appendTooltipBiblioField(bibElement, 'Fascicle number', bibRec.data_fascicle_number);
        this.appendTooltipBiblioField(bibElement, 'Volume', bibRec.data_volume);
        this.appendTooltipBiblioField(bibElement, 'Pages', bibRec.data_pages);
        this.appendTooltipBiblioField(bibElement, 'Conference', bibRec.data_conference);
        this.appendTooltipBiblioField(bibElement, 'Number of volumes', bibRec.data_num_volumes);
        this.appendTooltipBiblioField(bibElement, 'Date of dissertation', bibRec.data_dissertation_date);
        this.appendTooltipBiblioField(bibElement, 'School/University', bibRec.data_university);
        this.appendTooltipBiblioField(bibElement, 'Book series', bibRec.data_book_series);
        this.appendTooltipBiblioField(bibElement, 'Place of edition', bibRec.data_edition_place);
        this.appendTooltipBiblioField(bibElement,
            bibRec.fkidbiblio_types === 10 ? 'Date of Communication' : 'Date of edition', bibRec.data_edition_date);
        this.appendTooltipBiblioField(bibElement, 'Type of Communication', bibRec.__biblio_comm_types);
        this.appendTooltipBiblioField(bibElement, 'Event', bibRec.data_event);
        this.appendTooltipBiblioField(bibElement, 'Publisher', bibRec.data_publisher);
        if (bibRec.data_url) {
            this.appendTooltipBiblioField(bibElement, 'Url', bibRec.data_url);
            this.appendTooltipBiblioField(bibElement, 'Consultation date', bibRec.data_consultation_date);
        }
        this.appendTooltipBiblioField(bibElement, 'Notes', bibRec.notes_text);

        /* Load related bibliography links */
        if (bibRec.__linkedBiblio && bibRec.__linkedBiblio.length > 0) {
            const isPartOf_IdList = bibRec.__linkedBiblio
                .filter(bibLink => bibLink.fkidbiblio_rel_types === 5)
                .map(bibLink => bibLink.fkidbibliography_related);
            const isReviewOf_IdList = bibRec.__linkedBiblio
                .filter(bibLink => bibLink.fkidbiblio_rel_types === 2)
                .map(bibLink => bibLink.fkidbibliography_related);
            const biblioIdList = isPartOf_IdList.concat(isReviewOf_IdList);

            if (biblioIdList.length > 0) {
                this.loadingBiblioRel = true;
                this.subscriptions.push(
                    this.ceReferenceService.getBibReferences(biblioIdList).subscribe(
                        bibRefs => {
                            const isPartOf_Refs = bibRefs.filter(bibRef => isPartOf_IdList.indexOf((<BiblioRecord> bibRef.data).pkid) !== -1);
                            const isReviewOf_Refs = bibRefs.filter(bibRef => isReviewOf_IdList.indexOf((<BiblioRecord> bibRef.data).pkid) !== -1);

                            if (isPartOf_Refs.length > 0 && isPartOf_Refs.length === isPartOf_IdList.length) {
                                this.appendTooltipSection(bibElement, 'In');
                                for (const isPartOf_BibRef of isPartOf_Refs) {
                                    this.createTooltipBiblioElement(<BiblioRecord> isPartOf_BibRef.data, bibElement);
                                }
                            }
                            if (isReviewOf_Refs.length > 0 && isReviewOf_Refs.length === isReviewOf_IdList.length) {
                                this.appendTooltipSection(bibElement, 'Review of');
                                for (const isReviewOf_BibRef of isReviewOf_Refs) {
                                    this.createTooltipBiblioElement(<BiblioRecord> isReviewOf_BibRef.data, bibElement);
                                }
                            }
                        },
                        err => console.error(err)
                    )
                );
            }
        }

        return bibElement;
    }

    private appendTooltipSection(bibElement, section: string) {
        const sectionElement = this.renderer.createElement('p');
        this.renderer.setAttribute(sectionElement, 'style', 'font-weight: bold; padding-top: 1em');
        this.renderer.appendChild(sectionElement, this.renderer.createText(section));
        this.renderer.appendChild(bibElement, sectionElement);
    }

    private appendTooltipBiblioField(bibElement, label: string, value: string) {
        if (value) {
            const fieldElement = this.renderer.createElement('p');

            /* Field label */
            const labelElement = this.renderer.createElement('strong');
            this.renderer.appendChild(labelElement, this.renderer.createText(label + ': '));
            this.renderer.appendChild(fieldElement, labelElement);
            /* Field value */
            this.renderer.appendChild(fieldElement, this.renderer.createText(value));

            this.renderer.appendChild(bibElement, fieldElement);
        }
    }

    private unsubscribeAll() {
        this.loadingBiblioRel = false;
        for (const subscription of this.subscriptions) {
            if (subscription) {
                subscription.unsubscribe();
            }
        }
    }

}



// WEBPACK FOOTER //
// ./src/app/services/ce-tooltip.service.ts